import { FC, useState } from 'react';
import { Uploader } from 'rsuite';
import { RsButton } from 'components/Rsuite/Button';
import { Upload } from 'react-feather';
import * as XLSX from 'xlsx';
import { DefaultRdpManager, RdpManager } from 'core/rdp/RdpManager';
import _ from 'lodash';
import { toast } from 'react-toastify';
import styles from './importFile.module.scss';
import { useProductStore } from 'store/productStore';
import useSWRMutation from 'swr/mutation';

const defaultRdpManager: RdpManager = new DefaultRdpManager();

export const ImportProducts: FC<{
  retailId: string;
  availableProducts: number;
}> = ({ retailId, availableProducts }) => {
  const [loading, setLoading] = useState(false);
  const [uploadKey, setUploadKey] = useState(0);
  const { selectedProducts, changeSelectedProducts } = useProductStore();

  const fetchRetailProducts = async (
    _key: string,
    { arg: productIds }: { arg: string[] },
  ) => {
    try {
      if (!_.isEmpty(retailId) && !_.isNil(retailId)) {
        const pageable = { page: 1, sizePerPage: availableProducts };
        const productWithPagination = await defaultRdpManager.getRetailProducts(
          retailId,
          pageable,
          { productIds },
        );
        changeSelectedProducts(() => productWithPagination.products);
        return productWithPagination.products;
      }
    } catch (error) {
      console.error('Error fetching retail products:', error);
    } finally {
      setLoading(false);
    }
  };

  const { trigger: getProduct } = useSWRMutation(
    '/retails/products',
    fetchRetailProducts,
  );

  return (
    <Uploader
      key={uploadKey}
      action=''
      fileList={[]}
      autoUpload={false}
      multiple={false}
      fileListVisible={true}
      disableMultipart={true}
      removable={false}
      className={styles.importProducts}
      accept='.xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, .xls, application/vnd.ms-excel'
      onChange={async (file) => {
        setUploadKey((prev) => prev + 1);
        if (!file) {
          return;
        }

        try {
          const readArrayBuffer = (blob: Blob): Promise<ArrayBuffer> =>
            new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result as ArrayBuffer);
              reader.onerror = reject;
              reader.readAsArrayBuffer(blob);
            });

          if (!file[0].blobFile) {
            throw new Error('No file blob found');
          }
          const fileData = await readArrayBuffer(file[0].blobFile);
          const data = new Uint8Array(fileData);
          const workbook = XLSX.read(data, { type: 'array' });
          const worksheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[worksheetName];
          setLoading(true);
          if (!worksheet['!ref']) {
            throw new Error('Worksheet reference (!ref) is undefined');
          }

          const range = XLSX.utils.decode_range(worksheet['!ref']);
          const columnAData: string[] = [];

          for (let row = range.s.r + 1; row <= range.e.r; row++) {
            const cellRef = XLSX.utils.encode_cell({ c: 0, r: row });
            const cell = worksheet[cellRef];
            if (cell) {
              const value = String(cell.v).trim();
              if (value !== '' && !columnAData.includes(value)) {
                columnAData.push(value);
              }
            }
          }

          // Validate product codes: must be either all digits or start with "TP" followed by digits.
          columnAData.forEach((code) => {
            if (!/^\d+$/.test(code) && !/^TP\d+$/.test(code)) {
              toast.error('匯入失敗：商品ID錯誤');
              throw new Error(`Invalid product code found: ${code}`);
            }
          });
          const uniqueProductIds = Array.from(new Set(columnAData));
          // Filter out already selected products to avoid duplicates
          const newProductIds = uniqueProductIds.filter(
            (id) => !selectedProducts.some((prod) => prod.productId === id),
          );
          const totalProductIds = [
            ...selectedProducts.map((prod) => prod.productId),
            ...newProductIds,
          ];
          // getProduct only if there are new product IDs to fetch
          if (newProductIds.length === 0) {
            toast.info('匯入成功：未新增新商品');
            setLoading(false);
            return;
          }

          if (totalProductIds.length > availableProducts) {
            toast.error(`匯入失敗：已超過${availableProducts}品上限`);
            setLoading(false);
            return;
          }

          if (newProductIds.length > 0) {
            const productList = await getProduct(totalProductIds);
            // 如果 uniqueProductIds 有未匯入的商品，表示有部分商品ID無效，需提示用戶並將 uniqueProductIds 未匯入的 商品ID 顯示出來
            const fetchedProductIds = productList?.map(
              (product) => product.productId,
            );
            const invalidProductIds = uniqueProductIds.filter(
              (id) => !fetchedProductIds?.includes(id),
            );
            if (invalidProductIds.length > 0) {
              toast.warning(
                `部分匯入失敗（${
                  invalidProductIds.length
                }品）：錯誤ID ${invalidProductIds.join(', ')}`,
                {
                  autoClose: false,
                  closeOnClick: false,
                  closeButton: true,
                },
              );
            } else if (productList && productList.length > 0) {
              toast.success('匯入成功');
            }
          }
          // Further workbook processing can be added here if needed.
        } catch (error) {
          console.error('Error processing file upload:', error);
          setLoading(false);
        }
      }}
    >
      <RsButton className='!text-[#413993]' loading={loading}>
        <p className='text-sm'>
          <Upload width={20} height={20} className='mr-2 text-[#413993]' />
          批量上傳
        </p>
      </RsButton>
    </Uploader>
  );
};
